<template>
  <ul
    v-if="open"
    :id="id"
    role="listbox"
    class="absolute inset-x-0 top-full z-[70] overflow-hidden rounded-b-md bg-white py-1 shadow-lg"
  >
    <li
      v-for="(suggestion, index) in suggestions"
      :id="`${id}-option-${index}`"
      :key="`${suggestion.match_type}:${suggestion.keyword}`"
      role="option"
      :aria-selected="index === activeIndex"
      class="notranslate cursor-pointer px-3 py-2 text-sm text-[--color-neutral-black]"
      :class="{ 'bg-[--color-brand-forrest-green-05]': index === activeIndex }"
      translate="no"
      @mouseenter="emit('activate', index)"
      @pointerdown.prevent="emit('select', suggestion)"
    >
      <template
        v-for="(segment, segmentIndex) in highlightedSegments(suggestion.keyword)"
        :key="segmentIndex"
      >
        <strong v-if="segment.suggested">{{ segment.text }}</strong><span v-else>{{ segment.text }}</span>
      </template>
    </li>
  </ul>
  <span v-else class="hidden" aria-hidden="true" />
</template>

<script setup lang="ts">
import type { KeywordAutocompleteSuggestion } from '@/modules/layout/composables/keywordAutocomplete';

const props = defineProps<{
  activeIndex: number;
  id: string;
  open: boolean;
  query: string;
  suggestions: KeywordAutocompleteSuggestion[];
}>();

const emit = defineEmits<{
  activate: [index: number];
  select: [suggestion: KeywordAutocompleteSuggestion];
}>();

interface HighlightedSegment {
  suggested: boolean;
  text: string;
}

function highlightedSegments(keyword: string): HighlightedSegment[] {
  const query = props.query.trim().replace(/\s+/g, ' ');

  if (!query) return [{ suggested: false, text: keyword }];

  const phrasePattern = query
    .split(' ')
    .map(part => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('\\s+');
  const phraseMatch = new RegExp(phrasePattern, 'i').exec(keyword);

  if (!phraseMatch || phraseMatch.index === undefined) {
    return [{ suggested: false, text: keyword }];
  }

  const matchStart = phraseMatch.index;
  const matchEnd = matchStart + phraseMatch[0].length;

  return [
    { suggested: true, text: keyword.slice(0, matchStart) },
    { suggested: false, text: keyword.slice(matchStart, matchEnd) },
    { suggested: true, text: keyword.slice(matchEnd) },
  ].filter(segment => segment.text !== '');
}
</script>
